/**
 * A small popover: a trigger you click, and a panel that appears under it.
 *
 * It exists so the pickers stop taking up permanent room on the page. A dial
 * that is always open is a dial you scroll past on every visit to read one
 * number you set six months ago.
 *
 * Two dismissals, both required. Escape, because a keyboard user who opens
 * this by accident must be able to leave without committing anything. And a
 * click anywhere else, because a panel that only closes via its own button is
 * a panel people close by reloading the page.
 */

import { useEffect, useRef, useState } from 'react';

export function Popover({ label, title, disabled, children, wide }: {
  /** What the closed trigger reads. */
  label: string;
  /** Tooltip on the trigger. */
  title?: string;
  disabled?: boolean;
  /** Rendered inside the panel. Gets a `close` it can call once it has an answer. */
  children: (close: () => void) => React.ReactNode;
  wide?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      // Contains(), not target === panel: a click on a number INSIDE the panel
      // must not count as a click outside it.
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // Capture phase: a child that stops propagation should not be able to
    // trap the popover open.
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="pop-wrap" ref={wrap}>
      <button
        type="button"
        className={'pop-trigger' + (open ? ' on' : '')}
        disabled={disabled}
        title={title}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(!open)}
      >
        {label}
      </button>
      {open && (
        <div className={'pop-panel' + (wide ? ' wide' : '')} role="dialog">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
